import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/errors/use-toast';
import { completeTask, uncompleteTask } from '../database-tasks';
import { mapDbTaskToUiTask } from '../types';
import { resetTaskContextOnCompletion } from '@/hooks/goal-planner/task-context-service';
import { updateGoalCompletionStatus } from '@/hooks/goal-planner/database-goals';
import { useUser } from '@/hooks/auth/use-user';

/**
 * Custom hook for completing a task
 */
export const useCompleteTaskMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  
  return useMutation({
    mutationFn: async (taskId: string) => {
      const { data, error } = await completeTask(taskId);
      if (error) throw new Error(error.message);
      
      // Reset context_sent flag so AI knows about this completion
      try {
        await resetTaskContextOnCompletion(taskId);
      } catch (contextError) {
        console.error('Failed to reset task context on completion:', contextError);
        // Don't fail the whole operation for this
      }
      
      return data ? mapDbTaskToUiTask(data) : null;
    },
    onSuccess: async (data) => {
      // Invalidate project-specific task queries
      queryClient.invalidateQueries({ 
        queryKey: ['tasks', userId],
        exact: false 
      });
      
      // Invalidate TaskManager queries (completion affects assigned task count)
      if (user?.id) {
        queryClient.invalidateQueries({ 
          queryKey: ['task-manager-tasks', user.id],
          exact: false 
        });
        queryClient.invalidateQueries({ 
          queryKey: ['assigned-tasks-count', user.id],
          exact: false 
        });
        
        // Broad invalidation for all users who might be assigned to this task
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return (
              (Array.isArray(key) && key[0] === 'task-manager-tasks') ||
              (Array.isArray(key) && key[0] === 'assigned-tasks-count')
            );
          }
        });
      }
      
      // Update goal completion status if task is linked to a goal
      if (data?.goalId && user?.id) {
        try {
          await updateGoalCompletionStatus(data.goalId, user.id);
          // Invalidate goal queries to reflect updated completion status
          queryClient.invalidateQueries({ 
            queryKey: ['goals', user.id],
            exact: false 
          });
        } catch (error) {
          console.error('Failed to update goal completion status:', error);
          // Don't fail the whole operation for this
        }
      }
    },
    onError: (error) => {
      toast({
        title: "Error completing task",
        description: error.message || "Failed to complete task",
        variant: "error",
      });
    }
  });
};

/**
 * Custom hook for uncompleting a task
 */
export const useUncompleteTaskMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  
  return useMutation({
    mutationFn: async (taskId: string) => {
      const { data, error } = await uncompleteTask(taskId);
      if (error) throw new Error(error.message);
      return data ? mapDbTaskToUiTask(data) : null;
    },
    onSuccess: async (data) => {
      // Invalidate project-specific task queries
      queryClient.invalidateQueries({ 
        queryKey: ['tasks', userId],
        exact: false 
      });
      
      // Invalidate TaskManager queries (completion affects assigned task count)
      if (user?.id) {
        queryClient.invalidateQueries({ 
          queryKey: ['task-manager-tasks', user.id],
          exact: false 
        });
        queryClient.invalidateQueries({ 
          queryKey: ['assigned-tasks-count', user.id],
          exact: false 
        });
        
        // Broad invalidation for all users who might be assigned to this task
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return (
              (Array.isArray(key) && key[0] === 'task-manager-tasks') ||
              (Array.isArray(key) && key[0] === 'assigned-tasks-count')
            );
          }
        });
      }
      
      // Update goal completion status if task is linked to a goal
      if (data?.goalId && user?.id) {
        try {
          await updateGoalCompletionStatus(data.goalId, user.id);
          // Invalidate goal queries to reflect updated completion status
          queryClient.invalidateQueries({ 
            queryKey: ['goals', user.id],
            exact: false 
          });
        } catch (error) {
          console.error('Failed to update goal completion status:', error);
          // Don't fail the whole operation for this
        }
      }
    },
    onError: (error) => {
      toast({
        title: "Error uncompleting task",
        description: error.message || "Failed to uncomplete task",
        variant: "error",
      });
    }
  });
};
